import React, { useEffect, useState, useRef, useCallback } from "react";

import EmployeesListItem from "../../employeesListItem";
import Button from "../../elements/button";
import { baseRequest } from "../../../axiosconfig";
import { ROLES } from "../../../constants/roles";
import { hasUnitEditPermission } from "../../../utils/permissions";
import Loading from "../../loading";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import NewEmployee from "../../newEmployee";
import DeleteEmployeePopup from "../../deleteEmployeePopup";
import EmployeelistErrorPopup from "../../employeeListErrorPopup";
import DropDown from "../../elements/dropDown";
import { useContext } from "react";
import { UnsavedDataContext } from "../../../context";
import { UserContext } from "../../../userContext";
import "../../employeesList/employeesList.css";

export default function AdminEmployeesList() {
  const [newEmployee, setNewEmployee] = useState();
  const [employeesList, setEmployeesList] = useState([]);
  const [errorMessages, setErrorMessages] = useState("");
  const [deleteError, setDeleteError] = useState(null);
  // zaposleni sa aktivnim korisničkim nalogom: druga, izričita potvrda
  const [accountConfirm, setAccountConfirm] = useState(null);
  const [organization_units, setOrganization_units] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employeeToDelete, setEmployeeToDelete] = useState();
  const [activeEmployee, setActiveEmployee] = useState();
  const [loading, setLoading] = useState(false);
  const [emptyFieldsError, setEmptyFieldsError] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [sortOption, setSortOption] = useState({ id: "all", name: "ALL" });

  const [showDesignationDescription, setShowDesignationDescription] = useState({
    isOpen: "false",
    id: null,
  });

  // Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [nothingFound, setNothingFound] = useState(false);
  const searchTimeoutRef = useRef(null);

  const params = useParams();
  const navigate = useNavigate();

  const { setUnsavedData } = useContext(UnsavedDataContext);
  const { user } = useContext(UserContext);

  useEffect(() => {
    if (newEmployee || activeEmployee) {
      setUnsavedData(true);
    } else if (!newEmployee || !activeEmployee) {
      setUnsavedData(false);
    }
  }, [newEmployee, activeEmployee]);

  const employeesListTitles = [
    { id: 1, title: "Ime*" },
    { id: 2, title: "Prezime*" },
    { id: 3, title: "Organizaciona jedinica*" },
    { id: 4, title: "Naziv radnog mesta*" },
    { id: 5, title: "Opis radnog mesta" },
    { id: 6, title: "Opcije" },
  ];

  useEffect(() => {
    setLoading(true);
    const getEmployeeUrl =
      params.id === "0"
        ? "/employee/id/0/level/0"
        : `/employee/id/${params.id}/level/${params.level}`;

    baseRequest
      .get("/organization_unit/0")
      .then((res) => {
        setOrganization_units(res.data.data);
      })
      .catch((error) => { console.error("Failed to fetch organization units:", error); });
    baseRequest
      .get(`/designations/0/0`)
      .then((res) => {
        setDesignations(res.data.designations);
      })
      .catch((error) => { console.error("Failed to fetch designations:", error); });
    baseRequest
      .get(getEmployeeUrl)
      .then((res) => {
        setEmployeesList(res.data.data);
        setLoading(false);
      })
      .catch((error) => { console.error("Failed to fetch employees:", error); setLoading(false); });
  }, []);

  // Debounced search function
  const performSearch = useCallback(
    (term) => {
      setIsSearching(true);
      setNothingFound(false);

      let url = "/employees";
      const queryParams = new URLSearchParams();

      if (term) {
        queryParams.append("name", term);
      }
      // Include current org unit filter
      if (sortOption && sortOption.id !== "all") {
        queryParams.append("organization_unit_id", sortOption.id);
        queryParams.append("organization_unit_level", sortOption.level);
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      baseRequest
        .get(url)
        .then((res) => {
          setEmployeesList(res.data.data);
          setIsSearching(false);
          if (res.data.data.length === 0 && term) {
            setNothingFound(true);
          }
        })
        .catch(() => {
          setIsSearching(false);
        });
    },
    [sortOption]
  );

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (searchTerm.trim()) {
        performSearch(searchTerm);
      } else {
        clearSearch();
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  // Clear search function
  const clearSearch = () => {
    setSearchTerm("");
    setNothingFound(false);
    // Re-fetch employees for current org unit
    const getEmployeeUrl =
      sortOption.id === "all"
        ? "/employee/id/0/level/0"
        : `/employee/id/${sortOption.id}/level/${sortOption.level}`;
    baseRequest
      .get(getEmployeeUrl)
      .then((res) => {
        setEmployeesList(res.data.data);
      })
      .catch((error) => { console.error("Failed to fetch employees:", error); });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim()) {
      performSearch(searchTerm);
    } else {
      clearSearch();
    }
  };

  const addEmployeeToList = () => {
    setNewEmployee({
      first_name: "",
      last_name: "",
      organization_unit_name: "",
      designation_name: "",
      designation_id: "",
      description: "",
      adding: true,
      level: "",
      parent_id: "",
    });

    setActiveEmployee({
      activeUnit: {
        first_name: "",
        last_name: "",
        organization_unit: "",
        designation_name: "",
        designation_id: "",
        designation_description: "",
        adding: true,
        parent_id: "",
      },
      prevState: {
        first_name: "",
        last_name: "",
        organization_unit: "",
        designation_name: "",
        designation_id: "",
        designation_description: "",
        adding: true,
        parent_id: "",
      },
    });
  };

  const handleCreateEmployee = (payload) => {
    if (validateAllFields(payload)) {
      return true;
    }
    setLoading(true);
    baseRequest
      .post("/employee", payload)
      .then((res) => {
        setLoading(false);
        setEmployeesList((list) => {
          return [...list, res.data.data];
        });
        setNewEmployee(null);
        setActiveEmployee(null);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  function handleChangeEmployee(e, field, id) {
    setEmptyFieldsError(null);
    setActiveEmployee((employee) => {
      return {
        ...employee,
        activeUnit: {
          ...employee.activeUnit,
          [field]: e.target.value.trimStart(),
        },
      };
    });
    setEmployeesList((employees) => {
      const newEmployees = employees.map((employee) => {
        if (employee.id === id) {
          return { ...employee, [field]: e.target.value.trimStart() };
        }
        return employee;
      });
      return [...newEmployees];
    });
  }

  const handleChangeNewEmployee = (e, field) => {
    if (emptyFieldsError) {
      setEmptyFieldsError((list) => {
        return {
          ...list,
          emptyFields: list.emptyFields.filter((item) => item !== field),
        };
      });
    }

    setNewEmployee((employee) => {
      return { ...employee, [field]: e.target.value.trimStart() };
    });
    setActiveEmployee((employee) => {
      return {
        ...employee,
        activeUnit: {
          ...employee.activeUnit,
          [field]: e.target.value.trimStart(),
        },
      };
    });
  };

  function handleSetEditEmployee(payload) {
    if (validate()) {
      return;
    }
    setActiveEmployee({
      activeUnit: { ...payload, typing: true },
      prevState: payload,
    });
    setEmployeesList((employees) => {
      const newEmployees = employees.map((employee) => {
        if (employee.id === payload.id) {
          return { ...employee, typing: true };
        }
        const { typing, ...rest } = employee;
        return rest;
      });
      return [...newEmployees];
    });
  }

  const handleUpdateEmployee = (payload) => {
    if (payload.typing) {
      if (validateAllFields(payload)) {
        return;
      }
      setLoading(true);
      baseRequest
        .put("/employee", payload)
        .then((res) => {
          setEmployeesList((employees) => {
            const newEmployees = employees.map((employee) => {
              if (employee.id === payload.id) {
                return res.data.data;
              }
              return employee;
            });
            return [...newEmployees];
          });
          setLoading(false);
          setActiveEmployee(null);
          setErrorMessages(null);
        })
        .catch(() => {
          setLoading(false);
        });
    }
    if (payload.adding) {
      handleCreateEmployee(payload);
    }
  };

  const handleAddUnitToList = (employeeId, unit) => {
    setEmployeesList((employees) => {
      const newEmployees = employees.map((employee) => {
        if (employee.id === employeeId) {
          return {
            ...employee,
            organization_unit: unit.name,
            designation_name: "",
            designation_id: "",
            designation_description: "",
          };
        }
        return employee;
      });
      return [...newEmployees];
    });
  };

  const handleAddDesignationToList = (employeeId, designation) => {
    setEmployeesList((employees) => {
      const newEmployees = employees.map((employee) => {
        if (employee.id === employeeId) {
          return {
            ...employee,
            designation_name: designation.name,
            designation_id: designation.id,
            designation_description: designation.description,
          };
        }
        return employee;
      });
      return [...newEmployees];
    });
  };

  const disableAddButton = () => {
    return (
      employeesList.some((employee) => employee.typing || employee.editing) ||
      newEmployee
    );
  };

  const validateAllFields = (employee) => {
    setErrorMessages(null);
    const fieldsToValidate = {
      first_name: 1,
      last_name: 2,
      parent_id: 3,
      designation_id: 4,
    };
    const emptyFields = Object.keys(employee).filter((key) => {
      if (fieldsToValidate[key]) {
        if (!employee[key]) {
          return key;
        }
      }
    });

    setEmptyFieldsError(
      emptyFields.length ? { id: employee.id, emptyFields } : null
    );
    if (emptyFields.length) {
      return true;
    }
    return false;
  };

  const deleteEmployee = (payload) => {
    if (validate()) {
      return;
    }
    setEmployeeToDelete(payload);
  };

  const handleConfirmDeleteEmployee = (payload, deactivateUsers = false) => {
    setLoading(true);
    baseRequest
      .delete(`/employee/${payload.id}`, {
        params: deactivateUsers ? { deactivate_users: 1 } : undefined,
      })
      .then((res) => {
        setEmployeesList((employees) => {
          const newEmployees = employees.map((employee) => {
            if (employee.id === res.data.data.id) {
              return { ...employee, del: 1 };
            }
            return employee;
          });
          return newEmployees;
        });
        setLoading(false);
        setErrorMessages("");
        setDeleteError(null);
        setEmployeeToDelete(null);
        setAccountConfirm(null);
      })
      .catch((err) => {
        setLoading(false);
        setEmployeeToDelete(null);
        setAccountConfirm(null);
        // aktivan nalog: ništa nije obrisano, traži se izričita odluka
        if (err?.response?.data?.code === "EMPLOYEE_HAS_ACTIVE_USERS") {
          setAccountConfirm({ employee: payload, data: err.response.data });
          return;
        }
        setDeleteError(
          err?.response?.data?.message ||
            "Zaposleni nije obrisan. Pokušajte ponovo."
        );
      });
  };

  const handleSubmit = () => {
    if (employeesList.length && !disableAddButton()) {
      setLoading(true);
      baseRequest
        .post(
          "/employee",
          { employees: employeesList }
        )
        .then((res) => {
          setLoading(false);
          navigate("/register/poslovni-procesi");
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const calculateDisbledButton = () => {
    return !employeesList.length || disableAddButton() || errorMessages;
  };

  const handleSelectUnit = (employee) => {
    return function (value) {
      if (emptyFieldsError) {
        setEmptyFieldsError((list) => {
          return {
            ...list,
            emptyFields: list.emptyFields.filter(
              (item) => item !== "parent_id"
            ),
          };
        });
      }
      if (employee.adding) {
        setNewEmployee((obj) => {
          return {
            ...obj,
            organization_unit_name: value.value.name,
            parent_id: value.value.id,
            level: value.value.level,
            description: "",
            designation_id: "",
            designation_name: "",
          };
        });
        setActiveEmployee((obj) => {
          return {
            ...obj,
            activeUnit: {
              ...obj.activeUnit,
              organization_unit_name: value.value.name,
              parent_id: value.value.id,
              level: value.value.level,
              description: "",
              designation_id: "",
              designation_name: "",
            },
          };
        });
      }
      if (employee.typing) {
        setActiveEmployee((obj) => {
          return {
            ...obj,
            activeUnit: {
              ...obj.activeUnit,
              organization_unit_name: value.value.name,
              parent_id: value.value.id,
              level: value.value.level,
              description: "",
              designation_id: "",
              designation_name: "",
            },
          };
        });
        setEmployeesList((list) => {
          const newList = list.map((item) => {
            if (item.id === employee.id) {
              return {
                ...item,
                organization_unit_name: value.value.name,
                parent_id: value.value.id,
                level: value.value.level,
                description: "",
                designation_id: "",
                designation_name: "",
              };
            }
            return item;
          });
          return newList;
        });
      }
    };
  };

  const validate = () => {
    if (newEmployee || activeEmployee) {
      setErrorMessages("Imate nesačuvane podatke. Želite da sačuvate?");
      return true;
    }
  };

  const resetActiveEmployee = () => {
    setEmployeesList((list) => {
      const newList = list.map((item) => {
        if (item.id === activeEmployee.prevState.id) {
          const { typing, ...rest } = activeEmployee.prevState;

          return rest;
        }
        return item;
      });
      return newList;
    });
    setActiveEmployee(null);
    setNewEmployee(null);
  };

  const handleSelectDesignation = (employee) => {
    return function ({ value }) {
      if (emptyFieldsError) {
        setEmptyFieldsError((list) => {
          return {
            ...list,
            emptyFields: list.emptyFields.filter(
              (item) => item !== "designation_id"
            ),
          };
        });
      }
      setEmployeesList((list) => {
        const newList = list.map((item) => {
          if (item.id === employee.id) {
            return {
              ...item,
              description: value.description,
              designation_id: value.id,
              designation_name: value.name,
            };
          }
          return item;
        });
        return newList;
      });
    };
  };

  const deleteNewEmployee = () => {
    setNewEmployee(null);
    setActiveEmployee(null);
    setEmptyFieldsError(null);
    setErrorMessages(null);
    setEmployeeToDelete(null);
  };

  const setFilterOrgUnit = ({ value }) => {
    // baseRequest.get(`/employee/id/${value.id}/level/${value.level}`);
    setLoading(true);
    if (value.id === "all") {
      baseRequest
        .get(`/employee/id/0/level/0`)
        .then((res) => {
          setLoading(false);

          setEmployeesList(res.data.data);
        })
        .catch(() => {
          setLoading(false);
        });
      navigate(`/admin/lista-zaposlenih/0/0`);
    } else {
      baseRequest
        .get(`/employee/id/${value.id}/level/${value.level}`)
        .then((res) => {
          setLoading(false);

          setEmployeesList(res.data.data);
        })
        .catch(() => {
          setLoading(false);
        });
      navigate(`/admin/lista-zaposlenih/${value.id}/${value.level}`);
    }

    // setSearchParams({
    //   org_unit_id: value.id,
    //   level: value.level ?? "",
    // });
    setSortOption(value);
  };

  const filterOrganisationUnits = (unit, list) => {
    if (unit.id === "all") {
      return list;
    }
    return list.filter((el) => el.parent_id === unit.id);
  };

  const hasEditEmployeePermission = hasUnitEditPermission;

  const hasAddEmployeePermission = (user) => {
    if (user.role_id === ROLES.SUPER_ADMIN || user.role_id === ROLES.MANAGER) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <div className="employees-list-section" style={{ border: "none" }}>
        <div
          className="processes-header"
          style={{ justifyContent: "space-between", height: "51px" }}
        >
          <div className="sort-options-dropdown" style={{ width: "auto" }}>
            <div style={{ flex: "1" }}>Organizaciona jedinica:</div>
            <DropDown
              dropDownStyle={{ width: "auto" }}
              height={"49px"}
              list={organization_units.filter(
                (el) => el.has_designations === true
              )}
              selectedValue={sortOption.name}
              containerValue={{ alignItems: "baseline", fontWeight: "bold" }}
              paddingLeft={"5px"}
              menu={{ width: "200px" }}
              onChange={setFilterOrgUnit}
              isFilter={true}
            />
          </div>

          <div className="employees-search-container" style={{ border: "none", marginBottom: "0" }}>
            <div className="employees-search-input-wrapper">
              <span className="employees-search-icon">&#128269;</span>
              <input
                type="text"
                className="employees-search-input"
                placeholder="Pretražite"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {searchTerm && (
                <button className="employees-search-clear" onClick={clearSearch}>
                  ×
                </button>
              )}
            </div>
          </div>

          {/* <div className="sort-options-dropdown" style={{ width: "200px" }}>
            <div
              className="sort-options-dropdown-selected"
              onClick={() => {
                setShowDropdown((state) => !state);
              }}
            >
              <span style={{ marginRight: "7px", paddingLeft: "10px" }}>
                Filtriraj po:
              </span>

              <img
                src="/images/sidebararrow.png"
                className="sidebar-navigation-item-arrow"
              ></img>
            </div>

            {showDropdown && (
              <ul
                className="sort-options-dropdown-options"
                style={{ left: "0", zIndex: "100", flex: 1 }}
              >
                <li
                  className="sort-options-dropdown-options-item"
                  onClick={() => {
                    setShowDropdown(false);
                  }}
                >
                  Svi
                </li>

                <li
                  className="sort-options-dropdown-options-item"
                  onClick={() => {
                    setShowDropdown(false);
                  }}
                >
                  Rukovodilac
                </li>
                <li
                  className="sort-options-dropdown-options-item"
                  onClick={() => {
                    setShowDropdown(false);
                  }}
                >
                  Zaposleni
                </li>
              </ul>
            )}
          </div> */}
        </div>
        <div className="table-scroll">
        <ul
          className="employees-list-header"
          style={{
            display: "flex",
            alignItems: "center",
            borderRight: "1px solid  var(--secondaryColor)",
          }}
        >
          {employeesListTitles.map((item) => {
            return (
              <li key={item.id} className="employees-list-title">
                {item.title}
              </li>
            );
          })}
        </ul>
        <div className="employees-list-wrapper ">
          {nothingFound && (
            <div className="employees-nothing-found">
              Ništa nije pronađeno
            </div>
          )}
          {employeesList.map((employee) => {
            if (!employee.del)
              return (
                <EmployeesListItem
                  key={employee.id}
                  employee={employee}
                  organization_units={organization_units}
                  designations={designations}
                  handleChangeEmployee={handleChangeEmployee}
                  handleSetEditEmployee={handleSetEditEmployee}
                  handleAddUnitToList={handleAddUnitToList}
                  handleAddDesignationToList={handleAddDesignationToList}
                  deleteEmployee={deleteEmployee}
                  errorMessages={errorMessages}
                  validateAllFields={validateAllFields}
                  handleUpdateEmployee={handleUpdateEmployee}
                  emptyFieldsError={emptyFieldsError}
                  handleSelectUnit={handleSelectUnit}
                  handleSelectDesignation={handleSelectDesignation}
                  activeEmployee={activeEmployee}
                  showDesignationDescription={showDesignationDescription}
                  setShowDesignationDescription={setShowDesignationDescription}
                  hasEditEmployeePermission={hasEditEmployeePermission}
                  user={user}
                />
              );
          })}
        </div>
        </div>
        {newEmployee && (
          <NewEmployee
            employee={newEmployee}
            organization_units={organization_units}
            designations={designations}
            handleChangeNewEmployee={handleChangeNewEmployee}
            setNewEmployee={setNewEmployee}
            setActiveEmployee={setActiveEmployee}
            handleCreateEmployee={handleCreateEmployee}
            emptyFieldsError={emptyFieldsError}
            deleteNewEmployee={deleteNewEmployee}
            handleSelectUnit={handleSelectUnit}
            setEmptyFieldsError={setEmptyFieldsError}
            style={{
              borderTop:
                employeesList.length > 0
                  ? "none"
                  : "2px solid var(--mainColor)",
              border: "2px solid var(--mainColor)",
            }}
            sortOption={sortOption}
            hasEditEmployeePermission={hasEditEmployeePermission}
            user={user}
          />
        )}

        {hasAddEmployeePermission(user) && (
          <button
            className={
              !disableAddButton()
                ? "add-employee-button"
                : "add-employee-button add-employee-button-disabled"
            }
            style={{ marginLeft: "10px" }}
            onClick={addEmployeeToList}
            disabled={disableAddButton()}
          >
            <span>
              <img
                alt=""
                src="/images/plus.png"
                className={disableAddButton() ? "disabled-button" : ""}
              />
            </span>
            Dodajte zaposlenog
          </button>
        )}
      </div>

      {deleteError && (
        <div className="errormessage" style={{ margin: "10px 0" }}>
          {deleteError}
        </div>
      )}
      {errorMessages && (
        <>
          <div className="loading"></div>
          <EmployeelistErrorPopup
            message={errorMessages}
            setErrorMessage={setErrorMessages}
            resetActiveEmployee={resetActiveEmployee}
            activeEmployee={activeEmployee}
            handleUpdateEmployee={handleUpdateEmployee}
            handleCreateEmployee={handleCreateEmployee}
            newEmployee={newEmployee}
          />
        </>
      )}
      {employeeToDelete && (
        <>
          <div className="loading"></div>
          <DeleteEmployeePopup
            employeeToDelete={employeeToDelete}
            setEmployeeToDelete={setEmployeeToDelete}
            handleConfirmDeleteEmployee={handleConfirmDeleteEmployee}
            loading={loading}
          />
        </>
      )}
      {accountConfirm && (
        <>
          <div className="loading"></div>
          <DeleteEmployeePopup
            employeeToDelete={accountConfirm.employee}
            setEmployeeToDelete={() => setAccountConfirm(null)}
            handleConfirmDeleteEmployee={(employee) =>
              handleConfirmDeleteEmployee(employee, true)
            }
            loading={loading}
            message={`${accountConfirm.data.message} Nalog: ${(
              accountConfirm.data.users || []
            )
              .map((u) => u.email)
              .join(", ")}. Da li želite da obrišete zaposlenog i trajno deaktivirate nalog?`}
          />
        </>
      )}
      {loading && (
        <>
          <div className="loading"></div>
          <Loading isLoading={loading} />
        </>
      )}
    </>
  );
}
